import { useMemo, useState } from "react";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { publishPurchaseOrders } from "../../services";
import { PurchaseOrderListItem } from "../../types/purchase-order.type";
import { numberWithCommas } from "../../utils";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import ConfirmModal from "../ConfirmModal";
import { branchRefText } from "./data";

interface PublishBranchLine {
  companyId: string;
  branchText: string;
  total: number;
  unmatched: number;
}

interface PublishDialogProps {
  open: boolean;
  /**
   * ใบที่ติ๊กเลือกไว้ในหน้าปัจจุบัน · การเลือกจำกัดอยู่ที่หน้าเดียว จำนวน uuid ที่ส่งไป
   * จึงไม่มีทางเกินเพดานของ API · การส่งทั้งไฟล์นำเข้าทำที่จอ W2 ด้วย batch_uuid
   */
  purchaseOrders: PurchaseOrderListItem[];
  onClose: () => void;
  onPublished: () => void;
}

/**
 * W5 ส่งให้สาขา — สรุปว่าแต่ละสาขากำลังจะได้รับกี่ใบ และในนั้นยังไม่จับคู่ร้านกี่ใบ
 *
 * ไม่บล็อก: ใบที่ยังไม่จับคู่ร้านถูกส่งไปด้วย แล้วสาขาจับคู่เองที่จอ PO ของสาขา
 */
export default function PublishDialog({
  open,
  purchaseOrders,
  onClose,
  onPublished,
}: PublishDialogProps) {
  const [publishing, setPublishing] = useState<boolean>(false);

  // สรุปจาก "สิ่งที่ผู้ใช้ติ๊กเลือกไว้" ซึ่งเป็นค่าที่ API ส่งมาทั้งหมด ไม่ใช่การคำนวณ
  // ยอดขึ้นใหม่ · จำนวนที่ส่งสำเร็จจริงมาจากคำตอบของ API หลังกดยืนยัน
  const branchLines = useMemo<PublishBranchLine[]>(() => {
    const lines: PublishBranchLine[] = [];
    const indexByCompanyId = new Map<string, number>();

    purchaseOrders.forEach((row) => {
      // ใบที่ยังไม่ระบุสาขาไม่ถูกส่งมาที่นี่ (W1 กรองออกแล้ว และ backend ก็ข้ามให้)
      if (!row.branch) return;
      const companyId = row.branch.company_id;
      const isUnmatched = row.status === "UNMATCHED_CUSTOMER";
      const existingIndex = indexByCompanyId.get(companyId);

      if (existingIndex === undefined) {
        indexByCompanyId.set(companyId, lines.length);
        lines.push({
          companyId,
          branchText: branchRefText(row.branch),
          total: 1,
          unmatched: isUnmatched ? 1 : 0,
        });
        return;
      }

      const line = lines[existingIndex];
      line.total += 1;
      if (isUnmatched) line.unmatched += 1;
    });

    return lines;
  }, [purchaseOrders]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const {
        data: { data },
      } = await publishPurchaseOrders({
        uuids: purchaseOrders.map((row) => row.uuid),
      });

      if (data) {
        pushGlobalSuccess(
          `ส่งให้สาขาแล้ว ${numberWithCommas(data.published_count)} ใบ ` +
            `(ยังไม่จับคู่ร้าน ${numberWithCommas(data.unmatched_count)} ใบ)` +
            (data.skipped_no_branch_count > 0
              ? ` · ข้าม ${numberWithCommas(data.skipped_no_branch_count)} ใบที่ยังไม่ระบุสาขา`
              : ""),
        );
      }
      onPublished();
      onClose();
    } catch {
      // 403 (ไม่ใช่ HO) และความผิดพลาดอื่นถูกแจ้งโดย interceptor แล้ว
    } finally {
      setPublishing(false);
    }
  };

  return (
    <ConfirmModal
      title="ส่งให้สาขา"
      description={`ส่ง PO ${numberWithCommas(purchaseOrders.length)} ใบ ไปให้ ${numberWithCommas(branchLines.length)} สาขา`}
      open={open}
      loading={publishing}
      confirmLabel="ส่งให้สาขา"
      confirmDisabled={purchaseOrders.length === 0}
      onClose={onClose}
      onConfirm={handlePublish}
    >
      <Stack mt={1} gap={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>สาขา</TableCell>
              <TableCell align="right">จำนวนที่ส่ง</TableCell>
              <TableCell align="right">ยังไม่จับคู่ร้าน</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {branchLines.map((line) => (
              <TableRow key={line.companyId}>
                <TableCell>{line.branchText}</TableCell>
                <TableCell align="right">
                  {numberWithCommas(line.total)}
                </TableCell>
                <TableCell align="right">
                  {numberWithCommas(line.unmatched)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="body2" color="text.secondary">
          ใบที่ยังไม่จับคู่ร้านถูกส่งไปด้วย สาขาจับคู่ร้านเองได้ที่จอ PO ของสาขา ·
          ใบที่ส่งไปแล้วก่อนหน้านี้และใบที่ยังไม่ระบุสาขาจะถูกข้าม
        </Typography>
      </Stack>
    </ConfirmModal>
  );
}
